import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { NetworkHelper } from '../helpers';
import { SHARED_ENV } from '../tokens';

/**
 * Securities-backed loans.
 *
 * The desk lends a client cash against units they already hold: the units stay
 * on the client's balance but are reserved (frozen) while the loan is open. A
 * loan can pledge several holdings; each collateral line is one instrument at
 * one custodian.
 * A loan is resolved by
 *  - **charging the balance** — a debit in the loan currency, partial allowed;
 *    the collateral is released once nothing is outstanding, or
 *  - **selling the collateral** — an OTC or GTN sell of one collateral line,
 *    tagged with the loan. When
 *    it settles the net proceeds pay the loan down (at the desk FX rate when
 *    the sale is in another currency); anything left stays with the client, and
 *    a shortfall stays owed.
 *
 * Amounts are plain numbers in the loan / collateral units. The yearly
 * interest rate is recorded only; nothing accrues.
 *
 * Backend: `trading-be-1/services/loan.service.js`.
 */

/** `active`: collateral reserved. `liquidating`: a sell order is open. */
export type LoanStatus = 'active' | 'liquidating' | 'closed';

/** `open` adds both open statuses. */
export type LoanStatusFilter = 'any' | 'open' | LoanStatus;

export type LoanSaleKind = 'otc' | 'gtn';

export type LoanOrderStatus = 'open' | 'settled' | 'cancelled' | 'failed';

export type LoanEventType =
  | 'disbursement'
  | 'balance_charge'
  | 'sale_repayment'
  | 'settled_outside'
  | 'liquidation'
  | 'rereserve'
  | 'unreserve';

export interface LoanInstrument {
  tickerId: string;
  sourceId: string;
  name: string | null;
  isOtc: boolean;
  priceCurrencyId: string;
  assetClassId: string;
}

/** One pledged holding: an instrument at one custodian. */
export interface LoanCollateral {
  /** Holding currency, e.g. `AAPL~NSDQ`. */
  currency: string;
  /** Null = the GTN share. */
  custodian: string | null;
  quantity: number;
  /** Still frozen; units on an open sell order are not counted. */
  reservedQuantity: number;
  instrument: LoanInstrument | null;
  /** Where a sale of this line goes. */
  saleKind: LoanSaleKind | null;
}

/** Where a collateral line is held: its custodian, GTN, or "OTC" for an OTC instrument (no custodian). */
export function collateralCustodianLabel(c: Pick<LoanCollateral, 'custodian' | 'instrument'>): string {
  if (c.custodian) return c.custodian;
  return c.instrument?.isOtc ? 'OTC' : 'GTN';
}

export interface LoanOrder {
  orderId: string;
  /** The collateral line it sells. */
  collateralCurrency: string | null;
  custodian: string | null;
  kind: LoanSaleKind;
  type: 'market' | 'limit';
  quantity: number;
  price: number | null;
  status: LoanOrderStatus;
  filledQuantity: number;
  /** Net cash the sale credited, in `proceedsCurrency`. */
  proceeds: number;
  proceedsCurrency: string | null;
  /** Part of the proceeds taken for the loan. */
  repaid: number;
  /** What `repaid` covered, in the loan currency. */
  loanAmount: number;
  fxRate: number | null;
  /** Why a finished sale could not be applied (`failed`). */
  error: string | null;
  adminId: string | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface LoanEvent {
  type: LoanEventType;
  amount: number;
  currency: string;
  /** The collateral line, for events that move units. */
  custodian: string | null;
  loanAmount: number;
  fxRate: number | null;
  ledgerId: string | null;
  orderId: string | null;
  adminId: string | null;
  note: string | null;
  at: string;
}

export interface Loan {
  id: string;
  userId: string;
  adminId: string;
  collateral: LoanCollateral[];
  currency: string;
  principal: number;
  outstanding: number;
  repaid: number;
  interestRate: number | null;
  note: string | null;
  /** False when registered with "skip balance changes": no cash was credited. */
  disbursed: boolean;
  status: LoanStatus;
  orders: LoanOrder[];
  events: LoanEvent[];
  closedAt: string | null;
  closedBy: string | null;
  created: string;
  updated: string;
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  /** Row info for the admin's shared client cell (`UserModel.toRowInfo`). */
  userInfo: Record<string, any> | null;
  adminEmail: string | null;
}

export interface LoanListParams {
  page: number;
  itemsPerPage: number;
  userId?: string | null;
  status?: LoanStatusFilter | null;
  currency?: string | null;
}

export interface LoanListResponse {
  total: number;
  items: Loan[];
}

export interface CreateLoanCollateral {
  currency: string;
  /** Null / omitted = GTN. */
  custodian?: string | null;
  quantity: number;
}

export interface CreateLoanPayload {
  userId: string;
  /** One line per instrument and custodian. */
  collateral: CreateLoanCollateral[];
  amount: number;
  currency: string;
  interestRate?: number | null;
  note?: string | null;
  /** True: record the loan and freeze the collateral, but credit no cash. */
  skipBalance?: boolean;
}

export interface RepayLoanPayload {
  /** Omitted = as much of the outstanding as the available cash covers. */
  amount?: number | null;
  note?: string | null;
}

export interface LiquidateLoanPayload {
  /** The collateral line to sell. */
  collateralCurrency: string;
  custodian: string | null;
  quantity: number;
  orderType: 'market' | 'limit';
  price?: number | null;
  note?: string | null;
}

@Injectable({ providedIn: 'root' })
export class LoanService {
  private readonly env = inject(SHARED_ENV);
  private readonly networkHelper = inject(NetworkHelper);
  /** Already ends in `/api`. */
  private readonly baseUrl: string;

  constructor() {
    this.baseUrl = `${this.env.host}/admin/loans`;
  }

  list(params: LoanListParams): Observable<LoanListResponse> {
    const query: Record<string, string | number> = {
      page: params.page,
      itemsPerPage: params.itemsPerPage,
    };
    if (params.userId) query['userId'] = params.userId;
    if (params.status) query['status'] = params.status;
    if (params.currency) query['currency'] = params.currency;
    return this.networkHelper.get(this.baseUrl, { ...query });
  }

  getById(id: string): Observable<Loan> {
    return this.networkHelper.get(`${this.baseUrl}/${encodeURIComponent(id)}`);
  }

  /** Freezes the collateral and credits the cash. */
  create(payload: CreateLoanPayload): Observable<Loan> {
    return this.networkHelper.post(this.baseUrl, payload);
  }

  /** Debits the client's cash in the loan currency. */
  /** Closes a loan registered with skipBalance: no charge, the collateral is released. */
  close(id: string, payload: { note?: string } = {}): Observable<Loan> {
    return this.networkHelper.post(
      `${this.baseUrl}/${encodeURIComponent(id)}/close`,
      payload
    );
  }

  repay(id: string, payload: RepayLoanPayload): Observable<Loan> {
    return this.networkHelper.post(
      `${this.baseUrl}/${encodeURIComponent(id)}/repay`,
      payload
    );
  }

  /** Places the sell order; the loan is settled when it fills. */
  liquidate(id: string, payload: LiquidateLoanPayload): Observable<Loan> {
    return this.networkHelper.post(
      `${this.baseUrl}/${encodeURIComponent(id)}/liquidate`,
      payload
    );
  }
}
